import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BilldaLogo } from '@billda/shared-ui';
import { AuthBrandedSide } from '../organisms/AuthBrandedSide';
import { AuthFormSide } from '../organisms/AuthFormSide';
import { ThemeToggle } from '../../theme/ThemeToggle';

interface AuthLayoutProps {
  /**
   * Form content to render in the right side
   */
  children: React.ReactNode;
  /**
   * Variant determines the branded side message
   * @default 'login'
   */
  variant?: 'login' | 'invite';
  /**
   * Maximum width of the form container
   * @default 500
   */
  maxWidth?: number;
  /**
   * Organization name to display (for invite variant)
   */
  organizationName?: string;
}

/**
 * Template: Main authentication layout
 * Two-column flexbox layout with branded side and form side
 * Responsive: collapses to single column on mobile
 * Clean, flat design with border-based separation
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  variant = 'login',
  maxWidth = 500,
  organizationName,
}) => {
  const theme = useTheme();
  // Dark secondary dominant — matches the desktop GradientBackground, keeps white text readable
  const mobileGradient = `linear-gradient(160deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 60%, ${theme.palette.secondary.light} 100%)`;
  const amber = theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Theme Toggle - Top Right Corner */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <ThemeToggle size="medium" />
      </Box>

      {/* Branded Side - Hidden on mobile, 2/3 width on desktop */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 2,
          position: 'relative',
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        <AuthBrandedSide
          variant={variant}
          organizationName={organizationName}
        />
      </Box>

      {/* Mobile Hero Banner - Only show on mobile for login variant */}
      {variant === 'login' && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            pt: 5,
            pb: 4,
            px: 4,
            background: mobileGradient,
          }}
        >
          <BilldaLogo variant="secondary" width={120} />
          <Typography
            variant="body2"
            sx={{
              color: amber,
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            From site to payday.
          </Typography>
        </Box>
      )}

      {/* Form Side - Full width on mobile, 1/3 on desktop */}
      <Box
        sx={{
          flex: { xs: 'none', md: 1 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', md: 'center' },
          position: 'relative',
          bgcolor: 'background.default',
          pt: { xs: 2, md: 0 },
        }}
      >
        {/* Form Content */}
        <AuthFormSide maxWidth={maxWidth}>{children}</AuthFormSide>
      </Box>
    </Box>
  );
};
